import moment, { Moment } from "moment";

export const dateToString = (date: Moment | undefined): string => {
    if (!date || !date.isValid()) {
        return "";
    }
    return `${date.format("YYYY-MM-DD")}T00:00:00.000Z`;
};

/**
 * Parses a date string in the format "YYYY-MM-DDTHH:mm:ss".
 * This function does NOT handle timezone information in the input string (e.g., "+01:00").
 *
 * @param {Moment | undefined} date - The date string to parse.
 * @returns {Moment} - A valid Moment object if the date is valid, otherwise an invalid Moment object.
 */
export const parseDate = (date?: Moment): Moment => {
    return moment(date, "YYYY-MM-DDTHH:mm:ss", true).isValid()
        ? moment(date, "YYYY-MM-DDTHH:mm:ss")
        : moment.invalid();
};

/**
 * Parses a date string in ISO 8601 format.
 * This function can handle timezone information in the input string (e.g., "+01:00").
 *
 * @param {Moment | string | undefined} date - The date string to parse.
 * @returns {Moment} - A valid Moment object if the date is valid, otherwise an invalid Moment object.
 */
export const parseDateWithTimeZone = (
    date?: Moment | string,
    nullable?: boolean
): Moment | null => {
    return moment(date, moment.ISO_8601, true).isValid()
        ? moment(date)
        : nullable
          ? null
          : moment.invalid();
};

/**
 * Parses a date string by stripping timezone information and treating it as local time.
 * This is useful when the backend returns dates with timezone suffixes (like "Z")
 * but the datetime should be treated as local time, not UTC.
 *
 * @param {Moment | string | undefined} date - The date string to parse (may include timezone info).
 * @returns {Moment} - A valid Moment object if the date is valid, otherwise an invalid Moment object.
 */
export const parseDateIgnoreTimezone = (date?: Moment | string): Moment => {
    if (!date) {
        return moment.invalid();
    }

    // If it's already a Moment object, return it as is
    if (moment.isMoment(date)) {
        return date.isValid() ? date : moment.invalid();
    }

    // Strip timezone information (Z, +HH:mm, -HH:mm) from the string
    const dateWithoutTimezone = String(date).replace(/[Z]|[+-]\d{1,2}:?\d{2}$/g, "");

    // Parse using the standard format
    return moment(dateWithoutTimezone, "YYYY-MM-DDTHH:mm:ss", true).isValid()
        ? moment(dateWithoutTimezone, "YYYY-MM-DDTHH:mm:ss")
        : moment.invalid();
};
